import { useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Loader2 } from "lucide-react";
import { api } from "../../../lib/api";

const STATUS_META = {
  pendiente: ["Pendiente", "#94a3b8"],
  en_progreso: ["En progreso", "#f59e0b"],
  pendiente_auditoria: ["Por auditar", "#7c3aed"],
  auditada: ["Auditada", "#0ea5e9"],
  correccion_requerida: ["Corrección requerida", "#ef4444"],
  cerrada: ["Cerrada", "#10b981"],
};

const dot = (color) => L.divIcon({
  className: "", iconSize: [16, 16], iconAnchor: [8, 8],
  html: `<span style="display:block;width:14px;height:14px;border-radius:9999px;background:${color};border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.35)"></span>`,
});

const findVar = (vars, names) => (vars.find((v) => names.includes(v.code.toLowerCase())) || {}).code;

export default function MapTab({ plan }) {
  const [units, setUnits] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const mapRef = useRef(null);
  const mapObj = useRef(null);
  const layer = useRef(null);

  const vars = plan.variables || [];
  const latCode = findVar(vars, ["lat", "latitud", "latitude"]);
  const lngCode = findVar(vars, ["lng", "lon", "longitud", "longitude"]);
  const labelCode = plan.label_field || (vars[0] || {}).code;

  useEffect(() => {
    api.get(`/planning/plans/${plan.id}/work-units`).then((r) => setUnits(r.data)).catch(() => setUnits([]));
  }, [plan.id]);

  const geoUnits = useMemo(() => (units || []).filter((u) => {
    const la = parseFloat(u.values[latCode]), lo = parseFloat(u.values[lngCode]);
    return latCode && lngCode && !isNaN(la) && !isNaN(lo) && (la !== 0 || lo !== 0);
  }), [units, latCode, lngCode]);

  const counts = useMemo(() => {
    const c = {};
    geoUnits.forEach((u) => { c[u.status || "pendiente"] = (c[u.status || "pendiente"] || 0) + 1; });
    return c;
  }, [geoUnits]);

  useEffect(() => {
    if (!mapRef.current || mapObj.current || !geoUnits.length) return;
    const m = L.map(mapRef.current, { scrollWheelZoom: true }).setView([-34.6, -58.5], 9);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { attribution: "© OpenStreetMap", maxZoom: 19 }).addTo(m);
    mapObj.current = m;
    setTimeout(() => m.invalidateSize(), 200);
    return () => { m.remove(); mapObj.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geoUnits.length > 0]);

  useEffect(() => {
    const m = mapObj.current;
    if (!m) return;
    if (layer.current) layer.current.remove();
    const g = L.layerGroup();
    const bounds = [];
    geoUnits.forEach((u) => {
      if (statusFilter && (u.status || "pendiente") !== statusFilter) return;
      const la = parseFloat(u.values[latCode]), lo = parseFloat(u.values[lngCode]);
      const [lbl, color] = STATUS_META[u.status || "pendiente"] || ["Pendiente", "#94a3b8"];
      const extra = vars.filter((v) => ![latCode, lngCode, labelCode].includes(v.code)).slice(0, 4)
        .map((v) => `${v.name}: ${u.values[v.code] ?? "—"}`).join("<br/>");
      L.marker([la, lo], { icon: dot(color) })
        .bindPopup(`<b>${u.values[labelCode] ?? ""}</b><br/><span style="color:${color}">●</span> ${lbl}<br/>${extra}`)
        .addTo(g);
      bounds.push([la, lo]);
    });
    g.addTo(m);
    layer.current = g;
    if (bounds.length) m.fitBounds(bounds, { padding: [30, 30] });
  }, [geoUnits, statusFilter, latCode, lngCode, labelCode, vars]);

  if (units === null) return <div className="py-16 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-navy/30" /></div>;

  if (!latCode || !lngCode || geoUnits.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-navy/15 bg-white p-14 text-center" data-testid="map-empty">
        <p className="text-sm text-navy/50 font-medium">Sin coordenadas que mostrar</p>
        <p className="text-sm text-navy/40 mt-1 max-w-lg mx-auto">
          Agrega variables <b>lat</b> y <b>lng</b> (Configuración → Variables) y captura las coordenadas de cada {plan.work_unit_name?.toLowerCase() || "unidad"} — el mapa se activará automáticamente con colores por estado.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="map-tab">
      <div className="flex flex-wrap items-center gap-2">
        {Object.entries(STATUS_META).map(([k, [lbl, color]]) => counts[k] ? (
          <button key={k} onClick={() => setStatusFilter(statusFilter === k ? "" : k)} data-testid={`map-filter-${k}`}
            className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border transition-colors ${statusFilter === k ? "border-navy bg-navy text-cream" : "border-navy/15 bg-white text-navy/60 hover:border-violet"}`}>
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: color }} /> {lbl} ({counts[k]})
          </button>
        ) : null)}
        <span className="ml-auto text-xs text-navy/40">{geoUnits.length} de {units.length} con ubicación</span>
      </div>
      <div ref={mapRef} className="rounded-xl border border-navy/10 overflow-hidden" style={{ height: 520 }} data-testid="map-canvas" />
    </div>
  );
}
